/**
 * Precision 3D Curved Hero Grid Engine
 * Features:
 * 1. Authentic Curved TV Concave Geometry (Zero Corner Overlaps)
 * 2. 2D Drag & Auto-Drift
 * 3. Click-to-View Lightbox Modal
 *
 * The scroll-driven text/media/card reveal previously owned by this class
 * now lives in ./scroll-reveal.js (clip-path masking + layered parallax,
 * replacing the 3D-perspective rotateX/translateZ approach).
 * Architecture: Nirajan <https://nbk.com.np>
 */

import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export class CurvedHeroGrid {
  constructor() {
    this.heroSection = document.getElementById('intro') || document.getElementById('hero');
    this.viewport = document.querySelector('.curved-cylinder-viewport');
    this.columns = document.querySelectorAll('.curved-cylinder-col');

    // Lightbox Elements
    this.lightbox = document.getElementById('nbk-lightbox-modal');
    this.lightboxImg = document.getElementById('nbk-lightbox-img');
    this.lightboxTitle = document.getElementById('nbk-lightbox-title');
    this.lightboxClose = document.getElementById('nbk-lightbox-close');

    if (!this.viewport || this.columns.length === 0) return;

    this.totalCols = this.columns.length;

    // Horizontal angular state
    this.currentOffsetAngle = 0;
    this.targetOffsetAngle = 0;
    this.dragVelocityX = 0;
    this.autoSpeed = -0.038; // Continuous smooth auto-drift

    // Vertical panning state (2D Dragging)
    this.currentOffsetY = 0;
    this.targetOffsetY = 0;
    this.dragVelocityY = 0;
    this.maxVerticalPan = 150; // Bounds for vertical drag

    // Drag detection
    this.isDragging = false;
    this.startX = 0;
    this.startY = 0;
    this.lastX = 0;
    this.lastY = 0;
    this.totalDragDist = 0;

    this.isReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.init();
  }

  init() {
    this.updateDimensions();
    window.addEventListener('resize', () => this.updateDimensions(), { passive: true });

    this.init2DDragPhysics();
    this.initLightbox();
    this.initRenderLoop();
  }

  updateDimensions() {
    const width = window.innerWidth;
    if (width < 480) {
      this.radius = 750;
      this.cardWidth = 190;
      this.cardGap = 14;
    } else if (width < 768) {
      this.radius = 950;
      this.cardWidth = 230;
      this.cardGap = 18;
    } else if (width < 1440) {
      this.radius = 1300;
      this.cardWidth = 260;
      this.cardGap = 22;
    } else {
      this.radius = 1450;
      this.cardWidth = 280;
      this.cardGap = 24; // Exact corner clearance in 3D (Zero overlap!)
    }

    const pitch = this.cardWidth + this.cardGap;
    this.angleStep = (pitch / this.radius) * (180 / Math.PI);
    this.totalArc = this.totalCols * this.angleStep;
  }

  /* -------------------------------------------------------------
   * 1. 2D Drag-and-Drop (Horizontal & Vertical Pan)
   * ------------------------------------------------------------- */
  init2DDragPhysics() {
    const onStart = (clientX, clientY) => {
      this.isDragging = true;
      this.startX = clientX;
      this.startY = clientY;
      this.lastX = clientX;
      this.lastY = clientY;
      this.totalDragDist = 0;
      this.dragVelocityX = 0;
      this.dragVelocityY = 0;
      this.viewport.style.cursor = 'grabbing';
    };

    const onMove = (clientX, clientY) => {
      if (!this.isDragging) return;
      const deltaX = clientX - this.lastX;
      const deltaY = clientY - this.lastY;

      this.totalDragDist += Math.hypot(deltaX, deltaY);
      this.lastX = clientX;
      this.lastY = clientY;

      // 1. Horizontal Angular Drag
      const angleDelta = (deltaX / (2 * Math.PI * this.radius)) * 360 * 1.35;
      this.targetOffsetAngle += angleDelta;
      this.dragVelocityX = angleDelta;

      // 2. Vertical Pan Drag with Spring Clamping
      this.targetOffsetY += deltaY * 0.9;
      if (this.targetOffsetY > this.maxVerticalPan) {
        this.targetOffsetY = this.maxVerticalPan + (this.targetOffsetY - this.maxVerticalPan) * 0.2;
      } else if (this.targetOffsetY < -this.maxVerticalPan) {
        this.targetOffsetY =
          -this.maxVerticalPan + (this.targetOffsetY + this.maxVerticalPan) * 0.2;
      }
      this.dragVelocityY = deltaY;
    };

    const onEnd = (e) => {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.viewport.style.cursor = 'grab';

      // Click to View Lightbox
      if (this.totalDragDist < 7 && e.target) {
        const clickedCard = e.target.closest('.curved-preview-card');
        if (clickedCard) {
          const img = clickedCard.querySelector('img');
          if (img) {
            const title = clickedCard.getAttribute('data-title') || img.alt || 'Project Preview';
            const link = clickedCard.getAttribute('data-link') || '#projects';
            const btnText = clickedCard.getAttribute('data-btn-text') || 'VIEW PROJECT';

            let images = [];
            const rawImages = clickedCard.getAttribute('data-images');
            if (rawImages) {
              try {
                images = JSON.parse(rawImages);
              } catch (err) {
                console.error('Failed to parse project images', err);
              }
            }
            if (!images.length) {
              images = [{ url: img.src, alt: title }];
            }

            if (window.nbkLightbox) {
              window.nbkLightbox.open({
                images,
                index: 0,
                title,
                link,
                btnText,
              });
            } else {
              this.openLightbox(img.src, title, link, btnText);
            }
          }
        }
      }
    };

    // Mouse Events
    this.viewport.addEventListener('mousedown', (e) => onStart(e.clientX, e.clientY));
    window.addEventListener('mousemove', (e) => onMove(e.clientX, e.clientY));
    window.addEventListener('mouseup', onEnd);

    // Touch Events (Mobile)
    this.viewport.addEventListener(
      'touchstart',
      (e) => {
        if (e.touches.length > 0) onStart(e.touches[0].clientX, e.touches[0].clientY);
      },
      { passive: true },
    );
    window.addEventListener(
      'touchmove',
      (e) => {
        if (e.touches.length > 0) onMove(e.touches[0].clientX, e.touches[0].clientY);
      },
      { passive: true },
    );
    window.addEventListener('touchend', onEnd);
  }

  /* -------------------------------------------------------------
   * 2. Click-to-View Lightbox Modal
   * ------------------------------------------------------------- */
  initLightbox() {
    if (!this.lightbox) return;

    const closeHandler = () => {
      this.lightbox.classList.add('opacity-0', 'pointer-events-none');
      this.lightbox.classList.remove('opacity-100', 'pointer-events-auto');
      this.lightbox.setAttribute('aria-hidden', 'true');
      if (this.lightboxImg) this.lightboxImg.src = '';
    };

    if (this.lightboxClose) {
      this.lightboxClose.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        closeHandler();
      });
    }

    this.lightbox.addEventListener('click', (e) => {
      if (!e.target.closest('.nbk-lightbox-content')) {
        closeHandler();
      }
    });

    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.lightbox.classList.contains('pointer-events-none')) {
        closeHandler();
      }
    });
  }

  openLightbox(src, title, link = '') {
    if (window.nbkLightbox) {
      window.nbkLightbox.open({
        images: [{ url: src, alt: title }],
        index: 0,
        title,
        link: link || '#projects',
        btnText: 'VIEW PROJECT',
      });
      return;
    }
    if (!this.lightbox || !this.lightboxImg) return;
    this.lightboxImg.src = src;
    if (this.lightboxTitle) {
      this.lightboxTitle.textContent = title;
    }
    const lbCtaBtn = document.getElementById('nbk-lightbox-cta-btn');
    const lbCtaText = document.getElementById('nbk-lightbox-cta-text');
    if (lbCtaBtn) {
      lbCtaBtn.href = link || '#projects';
    }
    if (lbCtaText) {
      lbCtaText.textContent = 'VIEW PROJECT';
    }
    this.lightbox.classList.remove('opacity-0', 'pointer-events-none');
    this.lightbox.classList.add('opacity-100', 'pointer-events-auto');
    this.lightbox.setAttribute('aria-hidden', 'false');
  }

  /* -------------------------------------------------------------
   * 3. Continuous Render Loop: Authentic Curved TV Geometry
   * ------------------------------------------------------------- */
  initRenderLoop() {
    const render = () => {
      if (!this.isReducedMotion) {
        if (!this.isDragging) {
          // Horizontal auto-drift + inertia decay
          this.targetOffsetAngle += this.autoSpeed;
          this.targetOffsetAngle += this.dragVelocityX;
          this.dragVelocityX *= 0.93;

          // Vertical inertia decay + spring back to bounds
          this.targetOffsetY += this.dragVelocityY * 0.5;
          this.dragVelocityY *= 0.9;
          if (this.targetOffsetY > this.maxVerticalPan) {
            this.targetOffsetY += (this.maxVerticalPan - this.targetOffsetY) * 0.1;
          } else if (this.targetOffsetY < -this.maxVerticalPan) {
            this.targetOffsetY += (-this.maxVerticalPan - this.targetOffsetY) * 0.1;
          }
        }

        // Smooth angle and vertical interpolation
        this.currentOffsetAngle += (this.targetOffsetAngle - this.currentOffsetAngle) * 0.09;
        this.currentOffsetY += (this.targetOffsetY - this.currentOffsetY) * 0.09;

        this.columns.forEach((col, idx) => {
          // Continuous modulo wrapping across totalArc (Zero gaps anywhere)
          const baseAngle = idx * this.angleStep;
          let colAngle =
            (((baseAngle + this.currentOffsetAngle) % this.totalArc) + this.totalArc) %
            this.totalArc;
          if (colAngle > this.totalArc / 2) colAngle -= this.totalArc;

          const absAngle = Math.abs(colAngle);

          // Hide cards far beyond visual horizon
          if (absAngle > 70) {
            col.style.opacity = '0';
            col.style.pointerEvents = 'none';
            return;
          }

          const rad = (colAngle * Math.PI) / 180;
          const cylX = Math.sin(rad) * this.radius;

          // AUTHENTIC CURVED TV CONCAVE GEOMETRY:
          // Left & Right corners curve FORWARD toward the viewer (+Z)
          // Center screen is flat/receding in front of the viewer
          const cylZ = (1 - Math.cos(rad)) * (this.radius * 0.45);

          // Rotate inward toward the viewer focal point (Curved TV concave angle)
          const cylRotY = -colAngle;

          col.style.transform = `translate3d(${cylX}px, ${this.currentOffsetY}px, ${cylZ}px) rotateY(${cylRotY}deg)`;

          // Smooth Cosine S-Curve Easing for Showing and Hiding (No sudden pop)
          let edgeAlpha = 1;
          if (absAngle > 36 && absAngle <= 68) {
            const norm = (absAngle - 36) / 32;
            edgeAlpha = 0.5 * (1 + Math.cos(norm * Math.PI));
          } else if (absAngle > 68) {
            edgeAlpha = 0;
          }

          col.style.opacity = `${edgeAlpha.toFixed(3)}`;
          col.style.pointerEvents = edgeAlpha > 0.3 ? 'auto' : 'none';
        });
      }

      requestAnimationFrame(render);
    };

    requestAnimationFrame(render);
  }
}
